import { useCallback, useEffect, useState } from 'react';
import { useAuth, UserButton } from '@clerk/react';
import { Navigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.horizontl.io';

const styles = {
  page: {
    minHeight: '100vh',
    background: '#F7F8FA',
    color: '#1A1D27',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Inter", "Segoe UI", sans-serif',
  },
  header: {
    height: '64px',
    padding: '0 32px',
    background: '#FFFFFF',
    borderBottom: '1px solid #E8EBF0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  brand: { display: 'flex', alignItems: 'center', gap: '10px', fontWeight: '700' },
  mark: {
    width: '30px', height: '30px', borderRadius: '7px', background: '#1D9E75',
    color: '#FFFFFF', display: 'grid', placeItems: 'center', fontSize: '14px',
  },
  account: { display: 'flex', alignItems: 'center', gap: '12px' },
  org: {
    padding: '5px 11px', borderRadius: '18px', background: '#F0FAF7',
    border: '1px solid #C8EDE3', color: '#1D9E75', fontSize: '13px', fontWeight: '600',
  },
  container: { width: 'min(1080px, calc(100% - 48px))', margin: '48px auto 72px' },
  eyebrow: {
    color: '#1D9E75', fontSize: '12px', fontWeight: '700',
    textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '10px',
  },
  title: { fontSize: '32px', lineHeight: 1.18, letterSpacing: '-0.025em', margin: '0 0 10px' },
  intro: { color: '#5A6070', fontSize: '16px', lineHeight: 1.65, margin: '0 0 32px', maxWidth: '720px' },
  setupGrid: {
    display: 'grid', gridTemplateColumns: '258px minmax(0, 1fr)', gap: '20px',
    alignItems: 'start',
  },
  stepRail: {
    background: '#FFFFFF', border: '1px solid #DFE3E8', borderRadius: '12px',
    padding: 0, overflow: 'hidden',
  },
  stepButton: (active, locked) => ({
    width: '100%', display: 'grid', gridTemplateColumns: '30px 1fr', gap: '12px',
    alignItems: 'start', textAlign: 'left', border: 0, borderBottom: '1px solid #EEF0F3',
    borderLeft: active ? '3px solid #1D9E75' : '3px solid transparent',
    borderRadius: 0, padding: '18px 16px 17px 14px', margin: 0,
    background: active ? '#F3FAF8' : '#FFFFFF', cursor: locked ? 'not-allowed' : 'pointer',
    opacity: locked ? 0.48 : 1,
  }),
  stepIcon: (complete, active) => ({
    width: '26px', height: '26px', borderRadius: '50%', display: 'grid', placeItems: 'center',
    background: complete ? '#9BE8C4' : active ? '#1D9E75' : '#EEF1F5',
    color: complete ? '#083D2D' : active ? '#FFFFFF' : '#697080',
    fontWeight: '800', fontSize: '12px',
  }),
  railTitle: { display: 'block', fontWeight: '700', fontSize: '14px', lineHeight: 1.35 },
  railText: { display: 'block', color: '#777D8C', fontSize: '12px', lineHeight: 1.45 },
  railStatus: {
    display: 'block', marginTop: '6px', color: '#697080', fontSize: '10px',
    fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.045em',
  },
  workspace: {
    minHeight: '520px', background: '#FFFFFF', border: '1px solid #DFE3E8',
    borderRadius: '12px', padding: '34px 36px',
  },
  workspaceHeader: {
    display: 'flex', justifyContent: 'space-between', gap: '20px', alignItems: 'flex-start',
    paddingBottom: '24px', marginBottom: '24px', borderBottom: '1px solid #E8EBF0',
  },
  workspaceTitle: { fontSize: '25px', lineHeight: 1.25, margin: '0 0 8px', letterSpacing: '-0.015em' },
  workspaceIntro: { color: '#5A6070', fontSize: '15px', lineHeight: 1.6, margin: 0, maxWidth: '610px' },
  statusPill: (complete) => ({
    borderRadius: '999px', padding: '7px 11px', whiteSpace: 'nowrap', fontSize: '12px',
    fontWeight: '700', color: complete ? '#147A5A' : '#697080',
    background: complete ? '#EAF9F4' : '#F1F3F6',
  }),
  sectionLabel: {
    color: '#5A6070', fontSize: '12px', fontWeight: '750', textTransform: 'uppercase',
    letterSpacing: '0.055em', marginBottom: '12px',
  },
  choiceGrid: { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '12px' },
  choiceCard: (selected, disabled = false) => ({
    display: 'flex', gap: '12px', alignItems: 'flex-start', padding: '15px 16px',
    border: selected ? '1px solid #1D9E75' : '1px solid #D9DEE7', borderRadius: '8px',
    background: selected ? '#F4FBF9' : '#FFFFFF', opacity: disabled ? 0.55 : 1,
    cursor: disabled ? 'not-allowed' : 'pointer', minHeight: '68px', boxSizing: 'border-box',
  }),
  choiceTitle: { fontWeight: '700', fontSize: '15px', marginBottom: '4px' },
  choiceText: { color: '#777D8C', fontSize: '13px', lineHeight: 1.45 },
  formGroup: { marginBottom: '24px' },
  formLabel: { display: 'block', fontSize: '14px', fontWeight: '700', marginBottom: '9px' },
  largeSelect: {
    width: '100%', minHeight: '44px', border: '1px solid #CAD1DB', borderRadius: '8px',
    padding: '0 12px', background: '#FFFFFF', color: '#1A1D27', fontSize: '14px',
  },
  actionBar: {
    display: 'flex', gap: '10px', alignItems: 'center', paddingTop: '22px',
    marginTop: '24px', borderTop: '1px solid #E8EBF0',
  },
  card: {
    background: '#FFFFFF', border: '1px solid #E8EBF0', borderRadius: '12px',
    padding: '8px 24px', boxShadow: '0 8px 28px rgba(26,29,39,0.04)',
  },
  row: {
    display: 'grid', gridTemplateColumns: '34px 1fr auto', gap: '14px',
    alignItems: 'center', padding: '18px 0', borderBottom: '1px solid #F0F2F5',
  },
  number: {
    width: '30px', height: '30px', borderRadius: '50%', background: '#F0FAF7',
    color: '#1D9E75', display: 'grid', placeItems: 'center', fontWeight: '700', fontSize: '13px',
  },
  stepTitle: { fontWeight: '650', marginBottom: '3px' },
  stepText: { color: '#9094A0', fontSize: '13px' },
  status: { color: '#9094A0', fontSize: '12px', fontWeight: '600' },
  smallButton: {
    border: '1px solid #1D9E75', borderRadius: '7px', padding: '7px 12px',
    background: '#FFFFFF', color: '#1D9E75', fontWeight: '700',
    cursor: 'pointer', fontSize: '12px', whiteSpace: 'nowrap',
  },
  actions: { display: 'flex', alignItems: 'center', gap: '16px', marginTop: '24px' },
  button: {
    border: 0, borderRadius: '8px', padding: '11px 20px', background: '#1D9E75',
    color: '#FFFFFF', fontWeight: '700', cursor: 'pointer', fontSize: '14px',
  },
  secondary: { color: '#9094A0', fontSize: '13px' },
  error: {
    color: '#C94A2A', background: '#FFF1EE', border: '1px solid #F2C8BC',
    borderRadius: '8px', padding: '12px 14px', marginTop: '18px', fontSize: '13px',
  },
  success: {
    color: '#147A5A', background: '#F0FAF7', border: '1px solid #C8EDE3',
    borderRadius: '8px', padding: '12px 14px', marginTop: '18px', fontSize: '13px',
  },
  completionHero: {
    display: 'grid', gridTemplateColumns: '48px minmax(0, 1fr)', gap: '16px',
    alignItems: 'center', padding: '20px', background: '#F0FAF7',
    border: '1px solid #C8EDE3', borderRadius: '10px',
  },
  completionIcon: {
    width: '48px', height: '48px', borderRadius: '50%', background: '#9BE8C4',
    color: '#083D2D', display: 'grid', placeItems: 'center', fontSize: '22px',
    fontWeight: '800',
  },
  completionTitle: {
    margin: '0 0 5px', fontSize: '19px', lineHeight: 1.3, letterSpacing: '-0.01em',
  },
  summaryGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: '12px', marginTop: '22px',
  },
  summaryItem: {
    padding: '15px 16px', border: '1px solid #E1E5EB', borderRadius: '9px',
    background: '#FFFFFF',
  },
  summaryLabel: {
    color: '#777D8C', fontSize: '11px', fontWeight: '750', textTransform: 'uppercase',
    letterSpacing: '0.05em', marginBottom: '6px',
  },
  summaryValue: { color: '#1A1D27', fontSize: '14px', fontWeight: '700', lineHeight: 1.45 },
  pausedNotice: {
    display: 'grid', gridTemplateColumns: '24px minmax(0, 1fr)', gap: '11px',
    alignItems: 'start', marginTop: '22px', padding: '15px 16px', borderRadius: '9px',
    border: '1px solid #E8D7A5', background: '#FFF9E9', color: '#6D5520',
    fontSize: '13px', lineHeight: 1.55,
  },
  select: {
    border: '1px solid #D9DEE7', borderRadius: '7px', padding: '8px 10px',
    background: '#FFFFFF', minWidth: '220px', marginRight: '8px',
  },
  panel: {
    gridColumn: '2 / 4', marginTop: '12px', padding: '16px', borderRadius: '9px',
    border: '1px solid #E8EBF0', background: '#FAFBFC',
  },
  providerGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(145px, 1fr))',
    gap: '9px', marginTop: '12px',
  },
  providerCard: {
    padding: '11px 12px', border: '1px solid #D9DEE7', borderRadius: '8px',
    background: '#FFFFFF', fontSize: '13px', fontWeight: '650',
  },
  badge: {
    display: 'inline-block', marginLeft: '6px', padding: '2px 6px', borderRadius: '10px',
    background: '#F0F2F5', color: '#777D8C', fontSize: '10px', fontWeight: '700',
  },
  option: {
    display: 'flex', gap: '9px', alignItems: 'flex-start', padding: '9px 0',
    color: '#3E4452', fontSize: '13px', lineHeight: 1.45,
  },
  help: { color: '#777D8C', fontSize: '12px', lineHeight: 1.5, marginTop: '8px' },
  loading: { minHeight: '100vh', display: 'grid', placeItems: 'center', color: '#9094A0' },
};

const steps = [
  ['Connect a ticketing system', 'Required — choose where Horizontl receives and updates support tickets.'],
  ['Configure ticket handling', 'Required — choose priorities and how Dana delivers responses.'],
  ['Add context sources', 'Recommended — improve responses with approved documentation and technical context.'],
  ['Test and activate Dana', 'Required — confirm the complete ticket-to-response workflow.'],
];

const customerError = (error, fallback) => {
  const message = typeof error === 'string' ? error : error?.message;
  const known = {
    jira_authorization_unavailable: 'Jira could not be connected right now. Please try again in a few minutes.',
    jira_projects_unavailable: 'We could not load your Jira projects. Test the connection and try again.',
    jira_webhook_registration_failed: 'We could not finish setting up the selected projects. Your existing Jira connection has been kept.',
    confluence_authorization_unavailable: 'Confluence could not be connected right now. Please try again in a few minutes.',
    jira_connection_required: 'Connect and configure your ticketing system before adding Confluence.',
    workspace_test_ticket_required: 'Enter a complete Jira ticket key, such as KAN-142, or paste its Jira URL.',
    workspace_test_ticket_not_selected: 'Choose a ticket from one of the Jira projects selected during setup.',
    workspace_test_ticket_priority_excluded: 'This ticket has a priority Dana is not configured to process. Choose a matching ticket or update the selected priorities.',
    workspace_test_ticket_not_accessible: 'Horizontl could not access that Jira ticket. Check the key and your Jira permissions.',
    workspace_test_preview_expired: 'This preview expired. Fetch the ticket again before confirming the test.',
    workspace_test_confirmation_failed: 'Horizontl could not confirm this test. No response was posted to the customer.',
    workspace_test_public_delivery_failed: 'Jira did not confirm the public test response. Check the connected account’s comment permission and use a different test ticket before trying again.',
    workspace_test_delivery_requires_review: 'The delivery result is uncertain. Check the Jira ticket before continuing; Horizontl will not risk posting a duplicate public comment.',
    ticket_handling_configuration_changed: 'The response settings changed after this preview was generated. Fetch the ticket again to test the current settings.',
    workspace_test_required: 'Complete the controlled ticket test before activating Dana.',
    direct_response_acknowledgement_required: 'Confirm that direct responses are customer-visible before saving this mode.',
  };
  if (!message || message === 'Failed to fetch') {
    return 'We could not reach Horizontl. Check your connection and try again.';
  }
  if (known[message]) return known[message];
  if (/^[a-z0-9_]+$/.test(message)) return fallback;
  return message;
};

export default function Onboarding() {
  const { getToken } = useAuth();
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [jira, setJira] = useState({ status: 'not_connected' });
  const [jiraLoading, setJiraLoading] = useState(false);
  const [jiraProjects, setJiraProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [priorityOptions, setPriorityOptions] = useState([]);
  const [priorityScope, setPriorityScope] = useState('site');
  const [editingProjects, setEditingProjects] = useState(false);
  const [ticketingProvider, setTicketingProvider] = useState('jira');
  const [availableProviders, setAvailableProviders] = useState({
    ticketing: [{ id: 'jira', name: 'Jira', available: true }],
    knowledge: [{ id: 'confluence', name: 'Confluence', available: true }],
  });
  const [success, setSuccess] = useState(null);
  const [confluence, setConfluence] = useState({ status: 'not_connected', spaces: [] });
  const [confluenceSpaces, setConfluenceSpaces] = useState([]);
  const [selectedSpaceIds, setSelectedSpaceIds] = useState([]);
  const [confluenceLoading, setConfluenceLoading] = useState(false);
  const [ticketHandling, setTicketHandling] = useState({
    status: 'not_configured', priorities: ['*'], response_mode: 'draft_only',
    response_tone: 'professional', direct_response_acknowledged: false,
  });
  const [testStage, setTestStage] = useState('input'); // input | loading | preview | posted | complete
  const [testTicketKey, setTestTicketKey] = useState('');
  const [testLoading, setTestLoading] = useState(false);
  const [testError, setTestError] = useState(null);
  const [publicTestAcknowledged, setPublicTestAcknowledged] = useState(false);
  const [testDraft, setTestDraft] = useState({
    previewId: '', ticketSummary: '', ticketDescription: '', responseText: '',
    confidence: '', confidenceScore: null, confidenceExplanation: '',
    sources: [], delivery: 'draft_only',
  });
  const [activatedAt, setActivatedAt] = useState(null);
  const testTicketInput = testTicketKey.trim();
  const fullTicketKeyPattern = /^[A-Za-z][A-Za-z0-9_]*-\d+$/;
  const jiraBrowseUrlPattern = /^https:\/\/[^\s/]+\/browse\/[A-Za-z][A-Za-z0-9_]*-\d+\/?(?:[?#].*)?$/i;
  const testKeyFormatValid = fullTicketKeyPattern.test(testTicketInput)
    || jiraBrowseUrlPattern.test(testTicketInput);
  const fetchTestTicket = async () => {
    if (!testKeyFormatValid || testLoading) return;
    const ticketInput = fullTicketKeyPattern.test(testTicketInput)
      ? testTicketInput.toUpperCase()
      : testTicketInput;
    setTestLoading(true); setTestError(null); setError(null); setTestStage('loading');
    setPublicTestAcknowledged(false);
    try {
      const token = await getToken();
      const response = await fetch(`${API_URL}/api/onboarding/workspace-test/preview`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ticket_key: ticketInput }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.detail || 'Unable to prepare this test.');
      const resolvedTicketKey = data.ticket?.key || ticketInput;
      setTestTicketKey(resolvedTicketKey);
      setTestDraft({
        previewId: data.preview_id,
        ticketSummary: data.ticket?.summary || '',
        ticketDescription: data.ticket?.description || '',
        responseText: data.draft?.body || '',
        confidence: data.draft?.confidence || 'low',
        confidenceScore: data.draft?.confidence_score ?? null,
        confidenceExplanation: data.draft?.confidence_explanation || '',
        sources: data.draft?.sources || [],
        delivery: data.delivery || 'draft_only',
      });
      setTestStage('preview');
    } catch (err) {
      setTestError(customerError(err, 'Dana could not prepare this test. Try another Jira ticket.'));
      setTestStage('input');
    } finally { setTestLoading(false); }
  };

  const confirmTestDraft = async () => {
    if (!testDraft.previewId || testLoading) return;
    setTestLoading(true); setTestError(null); setError(null);
    try {
      const token = await getToken();
      const response = await fetch(`${API_URL}/api/onboarding/workspace-test/confirm`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ preview_id: testDraft.previewId }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.detail || 'Unable to confirm the workspace test.');
      window.location.assign(data.redirect_url || `/tickets?highlight=${data.ticket_id}&onboarding_test=1`);
    } catch (err) {
      setTestError(customerError(err, 'Horizontl could not confirm this test. Check the selected ticket before trying again.'));
    } finally { setTestLoading(false); }
  };

  const activateDana = async () => {
    if (testLoading) return;
    setTestLoading(true); setTestError(null); setError(null);
    try {
      const token = await getToken();
      const response = await fetch(`${API_URL}/api/onboarding/workspace-test/activate`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.detail || 'Unable to activate Dana.');
      setActivatedAt(data.activated_at || new Date().toISOString());
      setTestStage('complete');
    } catch (err) {
      setTestError(customerError(err, 'Dana could not be activated. Your workspace remains paused.'));
    } finally { setTestLoading(false); }
  };
  const [ticketHandlingLoading, setTicketHandlingLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeStep, setActiveStep] = useState(1);

  const loadAccount = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const response = await fetch(`${API_URL}/api/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.detail || 'Unable to load your workspace.');
      setAccount(data);

      const statusResponse = await fetch(`${API_URL}/api/onboarding/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const statusData = await statusResponse.json().catch(() => ({}));
      if (!statusResponse.ok) {
        throw new Error(statusData.detail || 'Unable to load your saved setup status.');
      }
      if (statusResponse.ok) {
        if (statusData.available_providers) setAvailableProviders(statusData.available_providers);
        const jiraStatus = statusData.jira || { status: 'not_connected' };
        setJira(jiraStatus);
        setSelectedProjectId((jiraStatus.projects || [])[0]?.id || '');
        const handlingStatus = statusData.ticket_handling || {
          status: 'not_configured', priorities: ['*'], response_mode: 'draft_only',
          response_tone: 'professional', direct_response_acknowledged: false,
        };
        setTicketHandling(handlingStatus);
        setPriorityOptions(handlingStatus.priority_options || []);
        setPriorityScope(handlingStatus.priority_catalog_scope || 'site');
        if (statusData.workspace_test?.status === 'passed') setTestStage('posted');
        if (handlingStatus.status === 'configured') setActiveStep(3);
        else if ((jiraStatus.projects || []).length) setActiveStep(2);
        if (jiraStatus.status !== 'not_connected') {
          const projectsResponse = await fetch(`${API_URL}/api/onboarding/jira/projects`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const projectsData = await projectsResponse.json().catch(() => ({}));
          if (projectsResponse.ok) {
            const availableProjects = projectsData.projects || [];
            setJiraProjects(availableProjects);
            setPriorityOptions(projectsData.priorities || handlingStatus.priority_options || []);
            setPriorityScope(projectsData.scope || handlingStatus.priority_catalog_scope || 'site');
            const selectedIds = new Set((jiraStatus.projects || []).map(project => project.id));
            const refreshedSelected = availableProjects.filter(project => selectedIds.has(project.id));
            if (refreshedSelected.length) {
              setJira(current => ({
                ...current, projects: refreshedSelected, project: refreshedSelected[0],
              }));
            }
            if (!(jiraStatus.projects || []).length && availableProjects.length === 1) {
              setSelectedProjectId(availableProjects[0].id);
            }
          }
        }
        const confluenceStatus = statusData.confluence || { status: 'not_connected', spaces: [] };
        setConfluence(confluenceStatus);
        setSelectedSpaceIds((confluenceStatus.spaces || []).map(space => space.id));
        if (handlingStatus.status === 'configured' && ['connected', 'skipped'].includes(confluenceStatus.status)) {
          setActiveStep(4);
        }
        if (confluenceStatus.status === 'authorized') {
          const spacesResponse = await fetch(`${API_URL}/api/onboarding/confluence/spaces`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const spacesData = await spacesResponse.json().catch(() => ({}));
          if (spacesResponse.ok) setConfluenceSpaces(spacesData.spaces || []);
        }
      }
    } catch (err) {
      setError(customerError(err, 'Unable to load your workspace.'));
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => { loadAccount(); }, [loadAccount]);

  const startSetup = async () => {
    if (starting) return;
    setStarting(true);
    setError(null);
    try {
      const token = await getToken();
      const response = await fetch(`${API_URL}/api/onboarding/start`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        const messages = {
          onboarding_owner_required: 'Only a workspace owner or administrator can start setup.',
          subscription_inactive: 'Your subscription must be active before setup can begin.',
        };
        throw new Error(messages[data.detail] || data.detail || 'Unable to start setup.');
      }
      setAccount(current => ({
        ...current,
        onboarding: {
          status: data.status,
          started_at: data.started_at,
          completed_at: data.completed_at,
        },
      }));
    } catch (err) {
      setError(customerError(err, 'Unable to start setup.'));
    } finally {
      setStarting(false);
    }
  };

  const connectJira = async () => {
    if (ticketingProvider !== 'jira') {
      setError('That ticketing provider is not available yet. Choose Jira to continue.');
      return;
    }
    setJiraLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const response = await fetch(`${API_URL}/api/onboarding/jira/authorize`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.authorization_url) {
        throw new Error(data.detail || 'Unable to start Jira authorization.');
      }
      window.location.assign(data.authorization_url);
    } catch (err) {
      setError(customerError(err, 'Unable to start Jira authorization.'));
      setJiraLoading(false);
    }
  };

  const testJira = async () => {
    setJiraLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const token = await getToken();
      const response = await fetch(`${API_URL}/api/onboarding/jira/test`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.detail || 'Jira connection test failed.');
      const projects = data.projects || [];
      setPriorityOptions(data.priorities || []);
      setPriorityScope(data.scope || 'site');
      setJira(current => ({
        ...current, status: 'connected', site_url: data.site_url,
        projects, project: projects[0] || null, webhook_active: true,
      }));
      setSuccess(`Connection successful — ${projects.length} Jira project${projects.length === 1 ? '' : 's'} verified and webhook active.`);
    } catch (err) {
      setError(customerError(err, 'Jira connection test failed.'));
    } finally {
      setJiraLoading(false);
    }
  };

  const configureJiraProjects = async () => {
    if (!selectedProjectId) {
      setError('Select the Jira project containing customer support tickets.');
      return;
    }
    setJiraLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const token = await getToken();
      const response = await fetch(`${API_URL}/api/onboarding/jira/projects`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ project_ids: [selectedProjectId] }),
      });
      const data = await response.json().catch(() => ({}));
      if (response.status === 405) {
        throw new Error('project_selection_unavailable');
      }
      if (!response.ok) throw new Error(data.detail || 'Unable to configure the Jira projects.');
      const projects = data.projects || [];
      setJira(current => ({
        ...current, status: 'connected', projects, project: projects[0] || null,
        webhook_active: true,
      }));
      setEditingProjects(false);
      setActiveStep(2);
      setSuccess(`Jira connected to ${projects[0]?.name || 'the selected project'}.`);
    } catch (err) {
      setError(customerError(err, 'We could not save your project selection. Please try again.'));
    } finally {
      setJiraLoading(false);
    }
  };

  const selectProject = projectId => setSelectedProjectId(projectId);

  const togglePriority = priorityKey => {
    setTicketHandling(current => {
      if (priorityKey === '*') {
        return { ...current, priorities: current.priorities.includes('*') ? [] : ['*'] };
      }
      const selected = current.priorities.includes('*') ? [] : current.priorities;
      return {
        ...current,
        priorities: selected.includes(priorityKey)
          ? selected.filter(value => value !== priorityKey)
          : [...selected, priorityKey],
      };
    });
  };

  const saveTicketHandling = async () => {
    setTicketHandlingLoading(true); setError(null); setSuccess(null);
    try {
      const token = await getToken();
      const response = await fetch(`${API_URL}/api/onboarding/ticket-handling`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          priorities: ticketHandling.priorities,
          response_mode: ticketHandling.response_mode,
          response_tone: ticketHandling.response_tone,
          direct_response_acknowledged:
            ticketHandling.response_mode === 'direct_response'
              ? ticketHandling.direct_response_acknowledged
              : false,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        const detail = data.detail;
        throw new Error(typeof detail === 'string' ? detail : 'Unable to save ticket handling.');
      }
      setTicketHandling(current => ({ ...current, ...data, status: 'configured' }));
      setActiveStep(3);
      setSuccess('Ticket handling saved. Dana will remain paused until the workspace test is completed.');
    } catch (err) { setError(customerError(err, 'Unable to save ticket handling.')); }
    finally { setTicketHandlingLoading(false); }
  };

  const connectConfluence = async () => {
    setConfluenceLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const response = await fetch(`${API_URL}/api/onboarding/confluence/authorize`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json().catch(() => ({}));
      if (response.status === 404 || response.status === 405) {
        throw new Error('confluence_authorization_unavailable');
      }
      if (!response.ok || !data.authorization_url) throw new Error(data.detail || 'Unable to authorize Confluence.');
      window.location.assign(data.authorization_url);
    } catch (err) {
      setError(customerError(err, 'Unable to authorize Confluence.'));
      setConfluenceLoading(false);
    }
  };

  const saveConfluenceSpaces = async () => {
    if (!selectedSpaceIds.length) return setError('Select at least one Confluence space.');
    setConfluenceLoading(true); setError(null); setSuccess(null);
    try {
      const token = await getToken();
      const response = await fetch(`${API_URL}/api/onboarding/confluence/spaces`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ space_ids: selectedSpaceIds }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.detail || 'Unable to configure Confluence.');
      setConfluence({ status: 'connected', spaces: data.spaces });
      setActiveStep(4);
      setSuccess(null);
    } catch (err) { setError(customerError(err, 'Unable to configure Confluence.')); }
    finally { setConfluenceLoading(false); }
  };

  const testConfluence = async () => {
    setConfluenceLoading(true); setError(null); setSuccess(null);
    try {
      const token = await getToken();
      const response = await fetch(`${API_URL}/api/onboarding/confluence/test`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.detail || 'Confluence connection test failed.');
      setSuccess(`Connection successful — ${data.spaces.length} Confluence space${data.spaces.length === 1 ? '' : 's'} accessible.`);
    } catch (err) { setError(customerError(err, 'Confluence connection test failed.')); }
    finally { setConfluenceLoading(false); }
  };

  const skipConfluence = async () => {
    setConfluenceLoading(true); setError(null); setSuccess(null);
    try {
      const token = await getToken();
      const response = await fetch(`${API_URL}/api/onboarding/knowledge-sources/skip`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.detail || 'Unable to skip this optional step.');
      setConfluence({ status: 'skipped', spaces: [] });
      setConfluenceSpaces([]);
      setSelectedSpaceIds([]);
      setActiveStep(4);
      setSuccess(null);
    } catch (err) { setError(customerError(err, 'Unable to skip this optional step.')); }
    finally { setConfluenceLoading(false); }
  };

  const toggleSpace = (spaceId) => {
    setSelectedSpaceIds(current => current.includes(spaceId)
      ? current.filter(id => id !== spaceId) : [...current, spaceId]);
  };

  if (loading) return <div style={styles.loading}>Loading your workspace…</div>;
  if (account?.onboarding?.status === 'completed') {
    return <Navigate to="/home" replace />;
  }

  const started = account?.onboarding?.status === 'in_progress';
  const canStart = ['owner', 'admin'].includes(account?.role);
  const jiraConfigured = jira.status === 'connected' && (jira.projects || []).length > 0;
  const handlingConfigured = ticketHandling.status === 'configured';
  const contextComplete = ['connected', 'skipped'].includes(confluence.status);
  const testPassed = ['posted', 'complete'].includes(testStage);
  const stepComplete = [false, jiraConfigured, handlingConfigured, contextComplete, testPassed];
  const stepLocked = [false, false, !jiraConfigured, !handlingConfigured, !handlingConfigured];
  const stepStatuses = [
    '',
    jiraConfigured ? 'Complete' : jira.status === 'not_connected' ? 'Not started' : 'Select projects',
    handlingConfigured ? 'Complete' : jiraConfigured ? 'Ready' : 'Locked',
    contextComplete ? (confluence.status === 'skipped' ? 'Skipped' : 'Complete') : handlingConfigured ? 'Optional' : 'Locked',
    testStage === 'complete' ? 'Activated' : testPassed ? 'Test passed' : handlingConfigured ? 'Ready for testing' : 'Locked',
  ];

  const prioritySummary = ticketHandling.priorities?.includes('*')
    ? 'All ticket priorities'
    : (ticketHandling.priorities || []).map(value => (
      priorityOptions.find(priority => priority.key === value)?.name || value
    )).join(', ');
  const responseSummary = ticketHandling.response_mode === 'direct_response'
    ? 'Direct customer-visible responses'
    : 'Drafts saved in Horizontl for review';
  const contextSummary = confluence.status === 'connected'
    ? `${confluence.spaces.length} Confluence space${confluence.spaces.length === 1 ? '' : 's'}`
    : 'Skipped for now';
  const workspaceHeading = activeStep === 4
    ? testStage === 'complete' ? 'Workspace setup complete' : 'Workspace connected'
    : steps[activeStep - 1][0];
  const workspaceDescription = activeStep === 4
    ? testStage === 'complete'
      ? 'Your integrations, settings and controlled test are complete. Dana is now active.'
      : 'Your integrations and ticket-handling preferences are saved. Dana is ready for a controlled workspace test.'
    : steps[activeStep - 1][1];

  const openStep = (stepNumber) => {
    if (stepLocked[stepNumber]) return;
    setActiveStep(stepNumber);
    setError(null);
    setSuccess(null);
  };

  return (
    <div style={styles.page}>
      <header style={styles.header}>
        <div style={styles.brand}><span style={styles.mark}>H</span>Horizontl</div>
        <div style={styles.account}>
          {account?.org_name && <span style={styles.org}>{account.org_name}</span>}
          <UserButton />
        </div>
      </header>

      <main style={styles.container}>
        <div style={styles.eyebrow}>Workspace setup</div>
        <h1 style={styles.title}>Set up your Horizontl workspace</h1>
        <p style={styles.intro}>
          Connect the systems Horizontl needs to analyse support tickets and prepare accurate technical responses. Your progress is saved automatically.
        </p>

        <div style={styles.setupGrid}>
          <nav style={styles.stepRail} aria-label="Workspace setup steps">
            {steps.map(([title], index) => {
              const stepNumber = index + 1;
              return (
                <button key={title} type="button" style={styles.stepButton(activeStep === stepNumber, stepLocked[stepNumber])}
                  onClick={() => openStep(stepNumber)} disabled={stepLocked[stepNumber]}>
                  <span style={styles.stepIcon(stepComplete[stepNumber], activeStep === stepNumber)}>
                    {stepComplete[stepNumber] ? '✓' : stepNumber}
                  </span>
                  <span>
                    <span style={styles.railTitle}>{title}</span>
                    <span style={{ ...styles.railStatus, color: stepComplete[stepNumber] ? '#147A5A' : '#697080' }}>
                      {stepStatuses[stepNumber]}
                    </span>
                  </span>
                </button>
              );
            })}
          </nav>

          <section style={styles.workspace}>
            <div style={styles.workspaceHeader}>
              <div>
                <div style={styles.eyebrow}>Step {activeStep} of 4</div>
                <h2 style={styles.workspaceTitle}>{workspaceHeading}</h2>
                <p style={styles.workspaceIntro}>{workspaceDescription}</p>
              </div>
              <span style={styles.statusPill(stepComplete[activeStep])}>{stepStatuses[activeStep]}</span>
            </div>

            {activeStep === 1 && (
              <>
                {!started && (
                  <div>
                    <p style={styles.workspaceIntro}>Start setup to connect the system where your support team receives customer tickets.</p>
                    <div style={styles.actionBar}>
                      {canStart ? (
                        <button type="button" style={styles.button} onClick={startSetup} disabled={starting}>
                          {starting ? 'Starting setup…' : 'Start setup →'}
                        </button>
                      ) : <span style={styles.secondary}>Ask a workspace owner or administrator to complete setup.</span>}
                    </div>
                  </div>
                )}

                {started && jira.status === 'not_connected' && (
                  <>
                    <div style={styles.sectionLabel}>Choose a ticketing system</div>
                    <div style={styles.choiceGrid}>
                      <label style={styles.choiceCard(ticketingProvider === 'jira')}>
                        <input type="radio" checked={ticketingProvider === 'jira'} onChange={() => setTicketingProvider('jira')} />
                        <span><div style={styles.choiceTitle}>Jira</div><div style={styles.choiceText}>Connect Jira securely through Atlassian OAuth.</div></span>
                      </label>
                      {availableProviders.ticketing.filter(provider => !provider.available).map(provider => (
                        <div key={provider.id} style={styles.choiceCard(false, true)}>
                          <span><div style={styles.choiceTitle}>{provider.name}<span style={styles.badge}>Coming soon</span></div><div style={styles.choiceText}>This integration is not selectable yet.</div></span>
                        </div>
                      ))}
                    </div>
                    <div style={{ padding: '16px 18px', background: '#F0FAF7', border: '1px solid #C8EDE3', borderRadius: '9px', color: '#355E52', fontSize: '14px', lineHeight: 1.55, marginTop: '18px' }}>
                      <div style={{ ...styles.choiceTitle, marginBottom: '8px' }}>What happens next</div>
                      <div>1. Atlassian opens securely in this browser.</div>
                      <div>2. Sign in to your Atlassian account, choose your Jira site and review the requested access.</div>
                      <div>3. Atlassian returns you to Horizontl to choose the project Dana should monitor.</div>
                      <div style={{ marginTop: '10px' }}>Horizontl never receives your Atlassian password. Connection credentials are encrypted and access can be removed at any time.</div>
                    </div>
                    <div style={styles.actionBar}>
                      <button type="button" style={styles.button} onClick={connectJira} disabled={jiraLoading || !canStart}>
                        {jiraLoading ? 'Opening Atlassian…' : 'Connect Jira →'}
                      </button>
                    </div>
                  </>
                )}

                {started && jira.status !== 'not_connected' && (!jiraConfigured || editingProjects) && (
                  <>
                    <div style={styles.sectionLabel}>Project to monitor</div>
                    <p style={{ ...styles.workspaceIntro, marginBottom: '18px' }}>Choose the Jira project that receives the customer support tickets Dana should handle. You can change it later in Settings.</p>
                    <div style={{ display: 'grid', gap: '10px' }}>
                      {jiraProjects.map(project => (
                        <label key={project.id} style={styles.choiceCard(selectedProjectId === project.id)}>
                          <input type="radio" name="jira-project" checked={selectedProjectId === project.id} onChange={() => selectProject(project.id)} />
                          <span>
                            <div style={styles.choiceTitle}>{project.name} ({project.key})</div>
                            <div style={styles.choiceText}>Available for draft or direct-response workflows.</div>
                          </span>
                        </label>
                      ))}
                    </div>
                    <div style={styles.actionBar}>
                      <button type="button" style={styles.button} onClick={configureJiraProjects} disabled={jiraLoading || !selectedProjectId}>
                        {jiraLoading ? 'Saving project…' : 'Save selected project →'}
                      </button>
                      {jiraConfigured && <button type="button" style={styles.smallButton} onClick={() => setEditingProjects(false)}>Cancel</button>}
                    </div>
                  </>
                )}

                {started && jiraConfigured && !editingProjects && (
                  <>
                    <div style={styles.sectionLabel}>Connected project</div>
                    <div style={{ display: 'grid', gap: '10px' }}>
                      {(jira.projects || []).map(project => (
                        <div key={project.id} style={styles.choiceCard(true)}>
                          <span style={styles.stepIcon(true, false)}>✓</span>
                          <span><div style={styles.choiceTitle}>{project.name} ({project.key})</div>
                            <div style={styles.choiceText}>Available for draft or direct-response workflows.</div></span>
                        </div>
                      ))}
                    </div>
                    <div style={styles.actionBar}>
                      <button type="button" style={styles.button} onClick={() => openStep(2)}>Continue to ticket handling →</button>
                      <button type="button" style={styles.smallButton} onClick={testJira} disabled={jiraLoading}>{jiraLoading ? 'Testing…' : 'Test connection'}</button>
                      <button type="button" style={styles.smallButton} onClick={() => setEditingProjects(true)} disabled={jiraLoading}>Manage projects</button>
                      <button type="button" style={styles.smallButton} onClick={connectJira} disabled={jiraLoading}>Reconnect</button>
                    </div>
                  </>
                )}
              </>
            )}

            {activeStep === 2 && jiraConfigured && (
              <>
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Ticket priorities Dana should handle</label>
                  <div style={{ display: 'grid', gap: '2px' }}>
                    <label style={styles.option}>
                      <input type="checkbox" checked={ticketHandling.priorities.includes('*')}
                        disabled={!priorityOptions.length} onChange={() => togglePriority('*')} />
                      <span>All available priorities</span>
                    </label>
                    {priorityOptions.map(priority => (
                      <label key={priority.key} style={styles.option}>
                        <input type="checkbox"
                          checked={ticketHandling.priorities.includes('*') || ticketHandling.priorities.includes(priority.key)}
                          disabled={ticketHandling.priorities.includes('*')}
                          onChange={() => togglePriority(priority.key)} />
                        <span>{priority.name}</span>
                      </label>
                    ))}
                    {!priorityOptions.length && <span style={styles.help}>No priorities were returned. Return to Step 1 and save the project selection again.</span>}
                  </div>
                  <div style={styles.help}>{priorityScope === 'projects'
                    ? 'These priorities are configured for the Jira projects selected in Step 1.'
                    : 'These priorities are available on your Jira site. Horizontl still validates every ticket before processing it.'}</div>
                </div>
                <div style={styles.formGroup}>
                  <div style={styles.formLabel}>Where should Dana place its response?</div>
                  <div style={{ display: 'grid', gap: '10px' }}>
                    <label style={styles.choiceCard(ticketHandling.response_mode === 'draft_only')}>
                      <input type="radio" name="response-mode" checked={ticketHandling.response_mode === 'draft_only'}
                        onChange={() => setTicketHandling(current => ({ ...current, response_mode: 'draft_only' }))} />
                      <span><div style={styles.choiceTitle}>Draft in Horizontl</div><div style={styles.choiceText}>Dana saves the draft in the Tickets page. A TSE reviews it and manually copies the approved response into the ticket.</div></span>
                    </label>
                    <label style={styles.choiceCard(ticketHandling.response_mode === 'direct_response')}>
                      <input type="radio" name="response-mode" checked={ticketHandling.response_mode === 'direct_response'}
                        onChange={() => setTicketHandling(current => ({ ...current, response_mode: 'direct_response', direct_response_acknowledged: false }))} />
                      <span><div style={styles.choiceTitle}>Direct response</div><div style={styles.choiceText}>Dana posts the generated response as a customer-visible comment in the connected ticketing system.</div></span>
                    </label>
                  </div>
                  {ticketHandling.response_mode === 'direct_response' && (
                    <label style={{ ...styles.pausedNotice, cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={Boolean(ticketHandling.direct_response_acknowledged)}
                        onChange={event => setTicketHandling(current => ({
                          ...current,
                          direct_response_acknowledged: event.target.checked,
                        }))}
                      />
                      <span>I understand that this mode sends customer-visible responses automatically after Dana is activated.</span>
                    </label>
                  )}
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Response tone</label>
                  <select style={styles.largeSelect} value={ticketHandling.response_tone}
                    onChange={event => setTicketHandling(current => ({ ...current, response_tone: event.target.value }))}>
                    <option value="professional">Professional</option><option value="friendly">Friendly</option>
                    <option value="formal">Formal</option><option value="technical">Technical</option>
                  </select>
                </div>
                <div style={styles.actionBar}>
                  <button type="button" style={styles.button} onClick={saveTicketHandling} disabled={ticketHandlingLoading || !ticketHandling.priorities.length || (ticketHandling.response_mode === 'direct_response' && !ticketHandling.direct_response_acknowledged)}>
                    {ticketHandlingLoading ? 'Saving settings…' : 'Save and continue →'}
                  </button>
                </div>
              </>
            )}

            {activeStep === 3 && handlingConfigured && (
              <>
                <div style={{ padding: '14px 16px', background: '#F0FAF7', border: '1px solid #C8EDE3', borderRadius: '9px', color: '#355E52', fontSize: '14px', lineHeight: 1.55, marginBottom: '22px' }}>
                  Context sources are optional. Dana can work from the ticket alone, but approved documentation improves accuracy and grounding.
                </div>
                {confluence.status === 'authorized' ? (
                  <>
                    <div style={styles.sectionLabel}>Choose Confluence spaces</div>
                    <div style={{ display: 'grid', gap: '10px' }}>{confluenceSpaces.map(space => (
                      <label key={space.id} style={styles.choiceCard(selectedSpaceIds.includes(space.id))}>
                        <input type="checkbox" checked={selectedSpaceIds.includes(space.id)} onChange={() => toggleSpace(space.id)} />
                        <span><div style={styles.choiceTitle}>{space.name}</div><div style={styles.choiceText}>{space.key ? `Space key: ${space.key}` : 'Approved Confluence space'}</div></span>
                      </label>
                    ))}</div>
                    <div style={styles.actionBar}><button type="button" style={styles.button} onClick={saveConfluenceSpaces} disabled={confluenceLoading || !selectedSpaceIds.length}>{confluenceLoading ? 'Saving spaces…' : 'Save selected spaces →'}</button></div>
                  </>
                ) : confluence.status === 'connected' ? (
                  <>
                    <div style={styles.choiceCard(true)}><span style={styles.stepIcon(true, false)}>✓</span><span><div style={styles.choiceTitle}>Confluence connected</div><div style={styles.choiceText}>{confluence.spaces.length} approved space{confluence.spaces.length === 1 ? '' : 's'} available to Dana.</div></span></div>
                    <div style={styles.actionBar}><button type="button" style={styles.button} onClick={() => openStep(4)}>Continue to workspace test →</button><button type="button" style={styles.smallButton} onClick={testConfluence} disabled={confluenceLoading}>{confluenceLoading ? 'Testing…' : 'Test connection'}</button></div>
                  </>
                ) : (
                  <>
                    <div style={styles.sectionLabel}>Documentation</div>
                    <div style={styles.choiceGrid}>
                      <div style={styles.choiceCard(true)}><span><div style={styles.choiceTitle}>Confluence <span style={{ ...styles.badge, color: '#147A5A', background: '#EAF9F4' }}>Available</span></div><div style={styles.choiceText}>Use documentation only from the spaces your team approves.</div></span></div>
                      {availableProviders.knowledge.filter(provider => !provider.available).map(provider => (
                        <div key={provider.id} style={styles.choiceCard(false, true)}><span><div style={styles.choiceTitle}>{provider.name}<span style={styles.badge}>Coming soon</span></div><div style={styles.choiceText}>Not selectable yet.</div></span></div>
                      ))}
                    </div>
                    <div style={{ padding: '16px 18px', background: '#F0FAF7', border: '1px solid #C8EDE3', borderRadius: '9px', color: '#355E52', fontSize: '14px', lineHeight: 1.55, marginTop: '18px' }}>
                      <div style={{ ...styles.choiceTitle, marginBottom: '8px' }}>What happens next</div>
                      <div>1. Atlassian opens again so you can review the additional Confluence access.</div>
                      <div>2. Horizontl requests permission to search and read documentation, and to create or update pages. It never requests permission to delete pages or spaces.</div>
                      <div>3. Atlassian returns you here to choose the Confluence spaces Dana may use.</div>
                      <div style={{ marginTop: '10px' }}>Your Jira connection remains active. Horizontl encrypts the connection credentials and restricts Dana to the spaces you select.</div>
                    </div>
                    <div style={styles.actionBar}>
                      <button type="button" style={styles.button} onClick={connectConfluence} disabled={confluenceLoading}>{confluenceLoading ? 'Opening Atlassian…' : 'Connect Confluence →'}</button>
                      <button type="button" style={styles.smallButton} onClick={skipConfluence} disabled={confluenceLoading}>Skip for now</button>
                    </div>
                  </>
                )}
              </>
            )}

            {activeStep === 4 && handlingConfigured && (
              <>
                {testStage !== 'complete' && <div style={styles.completionHero}>
                  <span style={styles.completionIcon}>✓</span>
                  <div>
                    <h3 style={styles.completionTitle}>You're all set for workspace testing</h3>
                    <div style={styles.choiceText}>Your core setup is saved. You can review any section from the setup panel.</div>
                  </div>
                </div>}

                {testStage !== 'complete' && <div style={styles.summaryGrid}>
                  <div style={styles.summaryItem}>
                    <div style={styles.summaryLabel}>Ticketing system</div>
                    <div style={styles.summaryValue}>Jira · {jira.project?.name || (jira.projects || [])[0]?.name || '1 project'}</div>
                  </div>
                  <div style={styles.summaryItem}>
                    <div style={styles.summaryLabel}>Response destination</div>
                    <div style={styles.summaryValue}>{responseSummary}</div>
                  </div>
                  <div style={styles.summaryItem}>
                    <div style={styles.summaryLabel}>Ticket priorities</div>
                    <div style={styles.summaryValue}>{prioritySummary}</div>
                  </div>
                  <div style={styles.summaryItem}>
                    <div style={styles.summaryLabel}>Context source</div>
                    <div style={styles.summaryValue}>{contextSummary}</div>
                  </div>
                </div>}

                {testStage !== 'complete' && <div style={styles.pausedNotice}>
                  <span aria-hidden="true">Ⅱ</span>
                  <div>
                    <strong>Dana is paused.</strong> No ticket responses will be posted until a controlled end-to-end test succeeds and you activate Dana.
                  </div>
                </div>}

                {testStage !== 'complete' && <div style={{ marginTop: '22px' }}>
                  <div style={styles.sectionLabel}>Test and activate Dana</div>

                  {testStage === 'input' && (
                    <div style={styles.formGroup}>
                      <label style={styles.formLabel}>Test ticket key</label>
                      <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                        <input
                          type="text"
                          placeholder="KAN-25 or paste Jira ticket URL"
                          value={testTicketKey}
                          onChange={event => { setTestTicketKey(event.target.value); setTestError(null); }}
                          style={{ ...styles.largeSelect, maxWidth: '220px' }}
                        />
                        <button type="button" style={styles.button} onClick={fetchTestTicket} disabled={!testKeyFormatValid || testLoading}>
                          {testLoading ? 'Fetching…' : 'Fetch ticket →'}
                        </button>
                      </div>
                      {testTicketKey && !testKeyFormatValid && (
                        <div style={styles.help}>Enter the complete ticket key, such as KAN-25, or paste the ticket's Jira browse URL.</div>
                      )}
                      {testKeyFormatValid && (
                        <div style={styles.help}>Use a ticket from one of your selected projects. Dana will read it and prepare a draft response — nothing is posted yet.</div>
                      )}
                      {testError && <div style={styles.error}>{testError}</div>}
                    </div>
                  )}

                  {testStage === 'loading' && (
                    <div style={styles.summaryItem}>
                      <div style={styles.summaryLabel}>Fetching {testTicketKey}</div>
                      <div style={styles.choiceText}>Dana is reading the ticket and preparing a draft response…</div>
                    </div>
                  )}

                  {testStage === 'preview' && (
                    <>
                      <div style={styles.summaryItem}>
                        <div style={styles.summaryLabel}>Ticket</div>
                        <div style={styles.summaryValue}>{testTicketKey} — {testDraft.ticketSummary}</div>
                        {testDraft.confidenceScore != null && <div style={{ ...styles.choiceText, marginTop: '8px' }}><strong>{testDraft.confidenceScore}% confidence</strong> — {testDraft.confidenceExplanation}</div>}
                      </div>
                      <div style={{ ...styles.summaryItem, marginTop: '12px' }}>
                        <div style={styles.summaryLabel}>Dana's response</div>
                        <div style={{ ...styles.choiceText, color: '#1A1D27', marginTop: '6px', whiteSpace: 'pre-wrap' }}>{testDraft.responseText}</div>
                      </div>
                      {!!testDraft.sources.length && (
                        <div style={{ ...styles.summaryItem, marginTop: '12px' }}>
                          <div style={styles.summaryLabel}>Supporting context</div>
                          <div style={styles.choiceText}>{testDraft.sources.map(source => source.title).join(' · ')}</div>
                        </div>
                      )}

                      <div style={styles.pausedNotice}>
                        <span aria-hidden="true">Ⅱ</span>
                        <div>{testDraft.delivery === 'public_on_confirmation'
                          ? `Nothing has been posted yet. Confirming will post this exact response publicly to ${testTicketKey}, where the customer can see it.`
                          : 'Confirming saves this response in Horizontl for human review. Nothing will be posted to Jira.'}</div>
                      </div>

                      {testDraft.delivery === 'public_on_confirmation' && (
                        <label style={{ ...styles.option, cursor: 'pointer' }}>
                          <input type="checkbox" checked={publicTestAcknowledged} onChange={event => setPublicTestAcknowledged(event.target.checked)} />
                          <span>I confirm this is a test ticket and understand this response will be visible to the customer.</span>
                        </label>
                      )}

                      {testError && <div style={styles.error}>{testError}</div>}

                      <div style={styles.actionBar}>
                        <button type="button" style={styles.button} onClick={confirmTestDraft} disabled={testLoading || (testDraft.delivery === 'public_on_confirmation' && !publicTestAcknowledged)}>
                          {testLoading ? 'Confirming…' : testDraft.delivery === 'public_on_confirmation' ? 'Post public test response →' : 'Confirm draft test →'}
                        </button>
                        <button type="button" style={styles.smallButton} onClick={() => { setTestStage('input'); setTestError(null); }}>Choose a different ticket</button>
                      </div>
                    </>
                  )}

                  {testStage === 'posted' && (
                    <>
                      <div style={styles.completionHero}>
                        <span style={styles.completionIcon}>✓</span>
                        <div>
                          <h3 style={styles.completionTitle}>Workspace test confirmed</h3>
                          <div style={styles.choiceText}>{testTicketKey
                            ? ticketHandling.response_mode === 'direct_response'
                              ? `Dana posted the test response publicly to ${testTicketKey}.`
                              : `Dana saved the test response for ${testTicketKey} in Horizontl for human review.`
                            : 'Your controlled ticket test succeeded. Activate Dana when you are ready.'}</div>
                        </div>
                      </div>
                      {testError && <div style={styles.error}>{testError}</div>}
                      <div style={styles.actionBar}>
                        <button type="button" style={styles.button} onClick={activateDana} disabled={testLoading}>
                          {testLoading ? 'Activating…' : 'Confirm and activate Dana →'}
                        </button>
                      </div>
                    </>
                  )}
                </div>}

                {testStage === 'complete' && (
                  <>
                    <div style={styles.completionHero}>
                      <span style={styles.completionIcon}>✓</span>
                      <div>
                        <h3 style={styles.completionTitle}>Dana is ready</h3>
                        <div style={styles.choiceText}>The controlled ticket test succeeded and your workspace has been activated.</div>
                      </div>
                    </div>

                    <div style={styles.summaryGrid}>
                      <div style={styles.summaryItem}><div style={styles.summaryLabel}>Jira project</div><div style={styles.summaryValue}>{jira.project?.key || (jira.projects || [])[0]?.key || '—'}</div></div>
                      <div style={styles.summaryItem}><div style={styles.summaryLabel}>Response destination</div><div style={styles.summaryValue}>{responseSummary}</div></div>
                      <div style={styles.summaryItem}><div style={styles.summaryLabel}>Ticket priorities</div><div style={styles.summaryValue}>{prioritySummary}</div></div>
                      <div style={styles.summaryItem}><div style={styles.summaryLabel}>Context source</div><div style={styles.summaryValue}>{contextSummary}</div></div>
                      <div style={styles.summaryItem}><div style={styles.summaryLabel}>Controlled test</div><div style={styles.summaryValue}>{testTicketKey} · In-app draft confirmed</div></div>
                      <div style={styles.summaryItem}><div style={styles.summaryLabel}>Activated</div><div style={styles.summaryValue}>{activatedAt ? new Date(activatedAt).toLocaleString() : 'Now'}</div></div>
                    </div>

                    <div style={{ marginTop: '22px' }}>
                      <div style={styles.sectionLabel}>What happens next</div>
                      <div style={styles.choiceCard(false)}>
                        <span style={styles.number}>1</span>
                        <span><div style={styles.choiceTitle}>Dana monitors your selected tickets</div><div style={styles.choiceText}>Responses follow the priorities, destination and tone chosen during setup. You can change these later in Settings.</div></span>
                      </div>
                    </div>

                    <div style={styles.actionBar}>
                      <button type="button" style={styles.button} onClick={() => window.location.assign('/home')}>Go to workspace →</button>
                    </div>
                  </>
                )}

                {testStage === 'input' && (
                  <div style={styles.actionBar}>
                    <button type="button" style={styles.smallButton} onClick={() => openStep(1)}>Review setup</button>
                  </div>
                )}
              </>
            )}

            {error && <div style={styles.error}>{error}</div>}
            {success && <div style={styles.success}>{success}</div>}
          </section>
        </div>
      </main>
    </div>
  );
}
